import React from 'react';
import {Box, styled, TextareaAutosize} from '@mui/material';
import {
  Body,
  designSystemComposition,
  designSystemToken,
} from '@lightricks/react-design-system';
import {scrollbarMixins} from '@contentlab/styles/mixins';
import translate from '@/utils/translate';

const TEXT_AREA_ROWS = 999;

const EditorContainer = styled(Box)(() => ({
  border: `1px solid ${designSystemToken('semantic.fg.tertiary')}`,
  borderRadius: '12px',
  display: 'flex',
  flexDirection: 'column',
  height: '100%',
  boxSizing: 'border-box',
  overflow: 'hidden',
}));

const TitleSection = styled(Box)(() => ({
  padding: '12px 0 8px 12px',
  flexShrink: 0,
  display: 'flex',
  alignItems: 'center',
  gap: '4px',
}));

const TitleSecondary = styled(Body)(() => ({
  color: designSystemToken('semantic.fg.secondary'),
  fontWeight: 700,
}));

const TitlePrimary = styled(Body)(() => ({
  color: designSystemToken('semantic.fg.primary'),
  fontWeight: 700,
}));

const StyledTextarea = styled(TextareaAutosize)(() => ({
  'boxSizing': 'border-box',
  'paddingInline': '12px',
  'width': '100%',
  'height': '100%',
  'border': 'none',
  'outline': 'none',
  'resize': 'none',
  'backgroundColor': 'transparent',
  ...scrollbarMixins.overrideScrollbar(),
  ...designSystemComposition('typography.body.xl'),
  '&::placeholder': {
    color: designSystemToken('semantic.fg.tertiary'),
  },
}));

interface ScriptEditorProps {
  testID?: string;
  title?: string;
  scriptNumber?: number;
  value: string;
  onChange: (value: string) => void;
  onBlur?: () => void;
  placeholder?: string;
}

function ScriptEditor({
  testID = 'script-editor',
  title,
  scriptNumber,
  value,
  onChange,
  onBlur,
  placeholder = translate('Write your script here...'),
}: ScriptEditorProps) {
  const renderTitle = () => {
    if (!title) return null;

    return (
      <>
        <TitleSecondary size="lg">
          {translate('Script')} {scriptNumber} ·
        </TitleSecondary>
        <TitlePrimary size="lg">{title}</TitlePrimary>
      </>
    );
  };

  return (
    <EditorContainer data-testid={testID}>
      <TitleSection>{renderTitle()}</TitleSection>
      <StyledTextarea
        placeholder={placeholder}
        value={value}
        onChange={(e: React.ChangeEvent<HTMLTextAreaElement>) =>
          onChange(e.target.value)
        }
        onBlur={onBlur}
        data-testid={`${testID}-textarea`}
        minRows={TEXT_AREA_ROWS}
        maxRows={TEXT_AREA_ROWS}
      />
    </EditorContainer>
  );
}

export default ScriptEditor;
